import { useContext, useRef, useState } from 'react';
import type { FC } from 'react';
import styled, { css } from 'styled-components';

import { Flex, registerIcon, defaultThemeProp } from '@pega/cosmos-react-core';
import * as chatIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/chat.icon';
import * as thumbsUpSolidIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/thumbs-up-solid.icon';
import * as thumbsUpIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/thumbs-up.icon';

import type { OpenGraphData } from '../OpenGraphPreview/OpenGraphPreview';

import type { FeedReplyProps } from './Feed.types';
import FeedAttachments from './FeedAttachments';
import FeedContent from './FeedContent';
import FeedContentHeader from './FeedContentHeader';
import FeedContentFooter from './FeedContentFooter';
import FeedEditRegion from './FeedEditRegion';
import { FeedContext } from './Feed.context';

registerIcon(chatIcon, thumbsUpSolidIcon, thumbsUpIcon);

export const StyledFeedReplyContainer = styled.div``;

StyledFeedReplyContainer.defaultProps = defaultThemeProp;

export const StyledReplyContent = styled.div(({ theme }) => {
  return css`
    margin-inline-start: 1rem;
    padding-inline-start: calc(3 * ${theme.base.spacing});
  `;
});

StyledReplyContent.defaultProps = defaultThemeProp;

const FeedReply: FC<FeedReplyProps> = (props: FeedReplyProps) => {
  const {
    id,
    avatarSrc,
    postTimestamp,
    content,
    attachments,
    username,
    fullname,
    liked,
    likes,
    likeCount,
    interactionsEnabled,
    icon,
    userStatus,
    maxContentHeight = Infinity,
    menuItems,
    onEdit,
    editConfig,
    edited = false,
    editTimestamp,
    onMouseEnter,
    onMouseLeave,
    onCommentClick,
    onLikeClick,
    onLikeCountInteraction,
    onLoadMoreLikes,
    likesLoading,
    onUserClick,
    ...restProps
  } = props;
  const variant = 'reply';

  const { autopilotInfo, onLinkRender } = useContext(FeedContext);
  const [openGraphData, setOpenGraphData] = useState<OpenGraphData | undefined>(undefined);
  const [emptyEditText, setEmptyEditText] = useState(false);
  const controllerRef = useRef(new AbortController());

  const adjustedUsername = username === autopilotInfo.label ? autopilotInfo.id : username;

  return (
    <StyledFeedReplyContainer
      onMouseEnter={() => {
        onMouseEnter?.({ id });
      }}
      onMouseLeave={() => {
        onMouseLeave?.({ id });
      }}
      {...restProps}
    >
      <FeedContentHeader
        variant={variant}
        id={id}
        fullname={fullname}
        username={adjustedUsername}
        postTimestamp={postTimestamp}
        avatarSrc={avatarSrc}
        icon={icon}
        userStatus={userStatus}
        menuItems={menuItems}
        edited={edited}
        editTimestamp={editTimestamp}
        onEdit={onEdit}
        onUserClick={onUserClick}
      />
      <StyledReplyContent>
        <Flex
          container={{ direction: 'column', pad: [0, editConfig ? 0 : 2, 0, 0] }}
          item={{ grow: 1 }}
        >
          {editConfig && typeof content === 'string' ? (
            <FeedEditRegion
              defaultValue={editConfig.defaultValue}
              variant={variant}
              id={id}
              content={content}
              onEditSubmit={e => {
                controllerRef.current.abort();
                setOpenGraphData(undefined);
                return editConfig.onEditSubmit(e);
              }}
              onEditCancel={editConfig.onEditCancel}
              onRewriteClick={editConfig.onRewriteClick}
              progress={editConfig.progress}
              banners={editConfig.banners}
              emptyEditText={emptyEditText}
              setEmptyEditText={setEmptyEditText}
              onUnmount={'onUnmount' in editConfig ? editConfig.onUnmount : undefined}
            />
          ) : (
            <FeedContent
              maxContentHeight={maxContentHeight}
              onLinkRender={
                onLinkRender
                  ? async href => {
                      controllerRef.current.abort();

                      controllerRef.current = new AbortController();

                      try {
                        const newOpenGraphData = await onLinkRender(
                          href,
                          controllerRef.current.signal
                        );
                        if (!controllerRef.current.signal.aborted) {
                          setOpenGraphData(newOpenGraphData);
                        }
                      } catch {
                        // If signal was aborted don't set state.
                      }
                    }
                  : undefined
              }
            >
              {content}
            </FeedContent>
          )}
          <FeedAttachments
            variant={variant}
            openGraphData={openGraphData}
            attachments={attachments}
          />
        </Flex>

        <FeedContentFooter
          variant={variant}
          id={id}
          username={adjustedUsername}
          liked={liked}
          likes={likes}
          likeCount={likeCount}
          interactionsEnabled={interactionsEnabled}
          likesLoading={likesLoading}
          onCommentClick={onCommentClick}
          onLikeClick={onLikeClick}
          onLikeCountInteraction={onLikeCountInteraction}
          onLoadMoreLikes={onLoadMoreLikes}
        />
      </StyledReplyContent>
    </StyledFeedReplyContainer>
  );
};

export default FeedReply;
